// app/frontend/components/ui/Buttons/ActionIconButton.tsx
import React, { useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import './ActionIconButton.css';

interface ActionIconButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  component?: any;
  style?: React.CSSProperties;
  disabled?: boolean;
  /** A soft white breathing glow, for one action worth noticing among plain icons. */
  glow?: boolean;
}

// forwardRef so the button can be a Popover.Target / Menu.Target (the
// calendar and directions popovers on the event page).
const ActionIconButton = React.forwardRef<HTMLButtonElement, ActionIconButtonProps>(({
  icon,
  label,
  onClick,
  href,
  target,
  rel,
  size = 'lg',
  component,
  style,
  disabled,
  glow = false,
}, ref) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip label={label}>
      <ActionIcon
        ref={ref}
        // Mantine only renders a link when told to; without this an href
        // gives a <button href> that ignores clicks.
        component={component ?? (href ? 'a' : undefined)}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        aria-label={label}
        variant="light"
        color="#EA580C"
        size={size}
        radius="md"
        disabled={disabled}
        className={['action-icon-tap', glow && !disabled ? 'action-icon-glow' : null].filter(Boolean).join(' ')}
        onMouseEnter={disabled ? undefined : () => setHovered(true)}
        onMouseLeave={disabled ? undefined : () => setHovered(false)}
        style={{
          transition: 'all 0.2s ease',
          transform: !disabled && hovered ? 'translateY(-2px)' : 'none',
          // A glowing button leaves its shadow to the stylesheet except on hover.
          boxShadow: !disabled && hovered ? '0 4px 8px rgba(234, 88, 12, 0.2)' : glow ? undefined : 'none',
          // Mantine's disabled state paints a light-theme grey fill, which reads
          // as a blank block on the dark UI. Keep the icon, dimmed, on no fill.
          ...(disabled ? { opacity: 0.35, cursor: 'not-allowed', backgroundColor: 'transparent', color: '#EA580C' } : {}),
          ...style,
        }}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
});
ActionIconButton.displayName = 'ActionIconButton';

export default ActionIconButton;
